import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable, isDevMode } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import {
  ErrorCode,
  LogLevel,
  Purchases,
  PurchasesError,
  type CustomerInfo,
  type Offering,
  type Package,
} from '@revenuecat/purchases-js';
import { RuntimeConfigService } from '../config/runtime-config.service';
import { LoggerService } from '../utils/logger.service';
import type { User } from '../../model/user';

/** Dashboard package identifiers (custom) and RC predefined fallbacks. */
export type AtplnestBillingPlan = 'monthly' | 'six_month' | 'yearly';

const DEFAULT_ENTITLEMENT_ID = 'atplnest Pro';
const DEFAULT_PADDLE_USER_ID_METADATA_KEY = 'app_user_id';

@Injectable({ providedIn: 'root' })
export class RevenueCatService {
  /** Tracks the last (rcUserId|entitlementId-hash) we sent to the backend per
   * session to avoid spamming the sync endpoint on every dashboard navigation. */
  private lastSyncedSignature: string | null = null;

  constructor(
    private runtime: RuntimeConfigService,
    private http: HttpClient,
  ) {}

  /** Entitlement checked for “Pro” access (must match RevenueCat dashboard). */
  get entitlementId(): string {
    return this.runtime.revenueCatEntitlementId?.trim() || DEFAULT_ENTITLEMENT_ID;
  }

  get isConfigured(): boolean {
    return Purchases.isConfigured();
  }

  /**
   * Call after login or when the shell loads with an existing session.
   * Uses stable backend user id namespace for cross-platform alignment.
   */
  async ensureIdentified(user: User | null): Promise<void> {
    const apiKey = this.runtime.revenueCatPublicApiKey?.trim();
    if (!apiKey || !user?.id) {
      return;
    }
    const appUserId = this.appUserIdFor(user.id);
    try {
      if (isDevMode()) {
        Purchases.setLogLevel(LogLevel.Debug);
      }
      if (!Purchases.isConfigured()) {
        Purchases.configure({ apiKey, appUserId });
        await Purchases.getSharedInstance().preload().catch(() => undefined);
      } else {
        const shared = Purchases.getSharedInstance();
        if (shared.getAppUserId() !== appUserId) {
          await shared.changeUser(appUserId);
          this.lastSyncedSignature = null;
        }
      }
      // Push entitlements to our backend so the access gate (trial vs paid)
      // sees the same Pro status the SDK reports. Without this, dev users
      // (no webhook URL) get stuck on the trial path forever.
      await this.pushCustomerInfoToBackend(user, appUserId);
    } catch (e) {
      LoggerService.error('[RevenueCat] ensureIdentified failed', e);
    }
  }

  /**
   * Mirrors the SDK's current CustomerInfo into the backend cache so
   * `AccessService` can authorize paid users without waiting for a RevenueCat
   * webhook (which never fires on localhost). Idempotent and safe to call
   * after every login / dashboard mount / purchase.
   */
  async pushCustomerInfoToBackend(user: User | null, appUserId?: string): Promise<void> {
    if (!user?.sid || !user.id || !Purchases.isConfigured()) {
      return;
    }
    const apiBase = this.runtime.apiBaseUrl?.replace(/\/$/, '');
    if (!apiBase) {
      return;
    }
    const info = await this.getCustomerInfo();
    if (!info) {
      return;
    }
    const rcUserId = appUserId ?? this.appUserIdFor(user.id);
    const activeIds = Object.keys(info.entitlements?.active ?? {}).sort().join(',');
    const expirations = Object.entries(info.entitlements?.active ?? {})
      .map(([id, ent]) => `${id}:${(ent as { expirationDate?: string | null }).expirationDate ?? ''}`)
      .sort()
      .join(',');
    const signature = `${user.id}|${rcUserId}|${activeIds}|${expirations}`;
    if (signature === this.lastSyncedSignature) {
      return;
    }
    try {
      const headers = new HttpHeaders({
        'Authorization': `Bearer ${user.sid}`,
        'Content-Type': 'application/json',
      });
      await firstValueFrom(
        this.http.post<{ synced: number }>(
          `${apiBase}/revenuecat/customer-info`,
          { rcUserId, customerInfo: info },
          { headers },
        ),
      );
      this.lastSyncedSignature = signature;
    } catch (e) {
      LoggerService.error('[RevenueCat] pushCustomerInfoToBackend failed', e);
    }
  }

  /** Tear down SDK when the auth session is cleared (logout / expired session). */
  closeIfConfigured(): void {
    if (!Purchases.isConfigured()) {
      return;
    }
    try {
      Purchases.getSharedInstance().close();
    } catch (e) {
      LoggerService.error('[RevenueCat] close failed', e);
    }
  }

  appUserIdFor(userId: number): string {
    const ns = this.runtime.revenueCatAppUserIdNamespace?.trim();
    if (ns && ns.length > 0) {
      return `${ns}_${userId}`;
    }
    return String(userId);
  }

  paddleUserIdMetadataKey(): string {
    return this.runtime.revenueCatPaddleUserIdMetadataKey?.trim() || DEFAULT_PADDLE_USER_ID_METADATA_KEY;
  }

  async getCustomerInfo(): Promise<CustomerInfo | null> {
    if (!Purchases.isConfigured()) {
      return null;
    }
    try {
      return await Purchases.getSharedInstance().getCustomerInfo();
    } catch (e) {
      LoggerService.error('[RevenueCat] getCustomerInfo failed', e);
      return null;
    }
  }

  /**
   * RevenueCat supplies this URL when the purchase platform supports customer
   * subscription management. Never construct a provider portal URL locally.
   */
  managementUrl(customerInfo: CustomerInfo | null): string | null {
    const candidate = (customerInfo as (CustomerInfo & {
      managementURL?: string | null;
      managementUrl?: string | null;
    }) | null)?.managementURL
      ?? (customerInfo as (CustomerInfo & { managementUrl?: string | null }) | null)?.managementUrl;
    if (!candidate) return null;
    try {
      const url = new URL(candidate);
      return url.protocol === 'https:' ? url.toString() : null;
    } catch {
      return null;
    }
  }

  /** Uses SDK helper (fetches customer info server-side). */
  async isEntitledToPro(): Promise<boolean> {
    if (!Purchases.isConfigured()) {
      return false;
    }
    try {
      return await Purchases.getSharedInstance().isEntitledTo(this.entitlementId);
    } catch (e) {
      LoggerService.error('[RevenueCat] isEntitledTo failed', e);
      return false;
    }
  }

  async getOfferings(): Promise<{ current: Offering | null; all: Offering[] } | null> {
    if (!Purchases.isConfigured()) {
      return null;
    }
    try {
      const o = await Purchases.getSharedInstance().getOfferings();
      const all = Object.values(o.all);
      const offeringId = this.runtime.revenueCatOfferingId?.trim();
      const current = offeringId ? o.all[offeringId] ?? null : o.current;
      return { current, all };
    } catch (e) {
      LoggerService.error('[RevenueCat] getOfferings failed', e);
      return null;
    }
  }

  /**
   * Resolve a package for plan keys configured in RevenueCat:
   * custom ids `monthly`, `six_month`, `yearly`, or predefined `$rc_*` slots.
   */
  resolvePackage(offering: Offering, plan: AtplnestBillingPlan): Package | null {
    const byId = offering.packagesById;
    if (plan === 'monthly') {
      return byId['monthly'] ?? offering.monthly;
    }
    if (plan === 'six_month') {
      return byId['six_month'] ?? offering.sixMonth;
    }
    return byId['yearly'] ?? offering.annual;
  }

  async purchasePlan(
    plan: AtplnestBillingPlan,
    opts: { htmlTarget?: HTMLElement; customerEmail?: string; appUserId?: string },
  ): Promise<CustomerInfo | null> {
    if (!Purchases.isConfigured()) {
      return null;
    }
    const offerings = await this.getOfferings();
    const offering = offerings?.current;
    if (!offering) {
      throw new Error('No RevenueCat offering available. Set a current offering in the dashboard.');
    }
    const rcPackage = this.resolvePackage(offering, plan);
    if (!rcPackage) {
      throw new Error(`No package found for plan "${plan}" (expected ids: monthly, six_month, yearly or $rc_*).`);
    }
    try {
      const currentUserId = opts.appUserId || Purchases.getSharedInstance().getAppUserId();
      const metadataKey = this.paddleUserIdMetadataKey();
      const result = await Purchases.getSharedInstance().purchase({
        rcPackage,
        htmlTarget: opts.htmlTarget,
        customerEmail: opts.customerEmail,
        metadata: {
          [metadataKey]: currentUserId,
        },
      });
      this.lastSyncedSignature = null;
      return result.customerInfo;
    } catch (e: unknown) {
      if (RevenueCatService.isUserCancelled(e)) {
        return null;
      }
      throw e;
    }
  }

  /**
   * Renders the RevenueCat-hosted paywall for the current (or configured) offering.
   * @see https://www.revenuecat.com/docs/web/paywalls
   */
  async presentPaywall(opts: {
    htmlTarget: HTMLElement;
    customerEmail?: string;
  }): Promise<CustomerInfo | null> {
    if (!Purchases.isConfigured()) {
      return null;
    }
    const offerings = await this.getOfferings();
    const offering = offerings?.current ?? undefined;
    try {
      const result = await Purchases.getSharedInstance().presentPaywall({
        htmlTarget: opts.htmlTarget,
        ...(offering ? { offering } : {}),
        customerEmail: opts.customerEmail,
      });
      this.lastSyncedSignature = null;
      return result.customerInfo;
    } catch (e: unknown) {
      if (RevenueCatService.isUserCancelled(e)) {
        return null;
      }
      throw e;
    }
  }

  static isUserCancelled(error: unknown): boolean {
    if (!(error instanceof PurchasesError)) {
      return false;
    }
    return error.errorCode === ErrorCode.UserCancelledError;
  }
}
